import React from 'react';
import type { NodeType } from '@mentimeter/ragnar-markdown';
import { RagnarMarkdown } from '@mentimeter/ragnar-markdown';
import { sanitizeUrl } from '@braintree/sanitize-url';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import { RemarkMath } from './MathParser';

interface MathT {
  value: string;
  children?: React.ReactNode;
}
interface NodeT {
  children: React.ReactNode;
  [x: string]: any;
}
interface LinkT extends NodeT {
  href: string;
}

export interface ComponentsT {
  Emoji?: React.FC<NodeT>;
  Em?: React.FC<NodeT>;
  Strong?: React.FC<NodeT>;
  Code?: React.FC<NodeT>;
  Math?: React.FC<MathT>;
  Link?: React.FC<LinkT>;
}

// Mapping from markdown node types (v4) to HTML element names (v10)
const convertAllowedTypesToElements = (allowedTypes: NodeType[]): string[] => {
  const typeToElementMap: Record<string, string[]> = {
    emphasis: ['em'],
    strong: ['strong'],
    inlineCode: ['code'],
    html: ['html'],
    delete: ['del'],
    link: ['a'],
    paragraph: ['p'],
    list: ['ol', 'ul'],
    listItem: ['li'],
  };

  const elements: string[] = [];
  allowedTypes.forEach((type) => {
    if (type === 'text') return; // text doesn't map to an HTML element
    const htmlElements = typeToElementMap[type];
    if (htmlElements) {
      elements.push(...htmlElements);
    }
  });

  // If we have inline elements, we need to include 'p' so they can be wrapped
  const inlineOnlyElements = ['em', 'strong', 'a', 'del'];
  const hasInlineElements = elements.some((el) =>
    inlineOnlyElements.includes(el),
  );
  const hasInlineCode = elements.includes('code') && !elements.includes('pre');
  const hasParagraph =
    allowedTypes.includes('paragraph') || elements.includes('p');

  if ((hasInlineElements || hasInlineCode) && !hasParagraph) {
    elements.push('p');
  }

  return elements;
};

const createComponents = (
  components: ComponentsT = {},
  source: string,
  disableMarkdown: boolean,
  disableLineBreaks: boolean,
) => ({
  // For all text nodes, check for tweemoji and set it dangerously
  text: ({ children }: NodeT) => {
    return disableMarkdown ? source : children;
  },
  p: ({ children }: NodeT) => {
    if (disableMarkdown) return source;
    // Do not render the <p> tag for this renderer.
    // remark-breaks handles line breaks, so we just return children directly
    return children as React.ReactElement;
  },
  em: ({ children }: NodeT) => {
    const C = components?.Em ?? 'em';
    return <C>{children}</C>;
  },
  strong: ({ children }: NodeT) => {
    const C = components?.Strong ?? 'strong';
    return <C>{children}</C>;
  },
  code: ({ children }: NodeT) => {
    const C = components?.Code ?? 'code';
    return <C>{children}</C>;
  },
  // Note: react-markdown v10 doesn't call custom components for custom node types from remark plugins
  // rehype-katex converts math nodes to HTML <span> and <div> elements with class "katex"
  // So we don't need custom math/inlineMath handlers - rehype-katex handles it
  a: ({ children, href }: LinkT) => {
    const C = components?.Link ?? 'a';
    const sanitizedUrl = sanitizeUrl(href);
    const isURL = /^https?:\/\/|^mailto:/i;
    // If href is an email address (auto-linked by remark-gfm), add mailto: prefix
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const finalHref = emailPattern.test(href) ? `mailto:${href}` : sanitizedUrl;
    if (isURL.test(sanitizedUrl) || emailPattern.test(href)) {
      return <C href={finalHref}>{children}</C>;
    } else {
      return children;
    }
  },
  br: () => {
    // Render line breaks when remark-breaks plugin converts single line breaks to <br />
    return <br />;
  },
  ol: ({ children }: NodeT) => {
    if (disableLineBreaks) return children;
    return <ol className="list-inside ">{children}</ol>;
  },
  ul: ({ children }: NodeT) => {
    if (disableLineBreaks) return children;
    return <ul className="list-inside">{children}</ul>;
  },
  li: ({ children }: NodeT) => {
    if (disableLineBreaks) return children;
    return <li>{children}</li>;
  },
});

interface StringParserT {
  source: string;
  components?: ComponentsT;
  disableMarkdown?: boolean;
  disableLineBreaks?: boolean;
  allowBlankLines?: boolean;
  allowHyperlinks?: boolean;
}

const plugins = [RemarkMath];
const MAX_CHARACTERS = 1000;

const StringParser = ({
  source,
  components,
  disableMarkdown = false,
  disableLineBreaks = false,
  allowBlankLines = false,
  allowHyperlinks = false,
}: StringParserT) => {
  const memoizedComponents = React.useMemo(
    () =>
      createComponents(components, source, disableMarkdown, disableLineBreaks),
    [components, source, disableMarkdown, disableLineBreaks],
  );

  const memoizedPlugins = React.useMemo(() => {
    const pluginList: unknown[] = [...plugins];
    // Add remark-breaks to convert single line breaks to <br /> tags
    // This is needed to render line breaks and blank lines
    if (!disableLineBreaks) {
      pluginList.push(remarkBreaks);
    }
    return pluginList;
  }, [disableLineBreaks]);

  // Always use rehype-katex to convert math nodes to HTML
  // react-markdown v10 doesn't call custom components for custom node types from remark plugins
  // So we need rehype-katex to convert math nodes to HTML first
  // Then we can intercept the HTML elements if needed
  const memoizedRehypePlugins = React.useMemo(() => {
    return [rehypeKatex];
  }, []);

  const memoizedAllowedElements = React.useMemo(() => {
    const allowedTypes: NodeType[] = [
      'emphasis',
      'text',
      'strong',
      'inlineCode',
      'html',
      'delete',
      'list',
      'listItem',
    ];
    if (allowHyperlinks) allowedTypes.push('link');
    if (!disableLineBreaks) {
      allowedTypes.push('paragraph');
    }
    const elements = convertAllowedTypesToElements(allowedTypes);
    // Add 'br' to allowed elements when line breaks are enabled
    // remark-breaks will create <br /> tags for single line breaks
    if (!disableLineBreaks && !elements.includes('br')) {
      elements.push('br');
    }
    // rehype-katex converts math nodes to HTML <span> (inline) and <div> (block) elements
    // We need to allow these elements for KaTeX to render
    if (!elements.includes('span')) {
      elements.push('span');
    }
    if (!elements.includes('div')) {
      elements.push('div');
    }
    return elements;
  }, [disableLineBreaks, allowHyperlinks]);

  const text = React.useMemo(() => {
    if (typeof source !== 'string') {
      return null;
    }
    if (source.length > MAX_CHARACTERS) {
      return source;
    }
    let processedText = source;

    // Preprocess mailto: links - remark-gfm auto-links email addresses but not mailto: prefixed strings
    // Convert mailto:email@domain.com to just email@domain.com so it gets auto-linked
    // The href will be set correctly by the link component
    if (allowHyperlinks) {
      processedText = processedText.replace(
        /mailto:([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        '$1',
      );
    }

    // modify text so markdown recognizes blank lines
    if (allowBlankLines) {
      // split the text before and after every linebreak
      const modifiedSegments = processedText.split(/(\n)/).map((segment) =>
        // an empty segment represents a blank line -> add a non-breaking space
        segment === '' ? '&nbsp;' : segment,
      );
      return modifiedSegments.join('');
    }
    return processedText;
  }, [source, allowBlankLines, allowHyperlinks]);

  if (text === null) {
    return null;
  }

  if (text.length > MAX_CHARACTERS) {
    return source;
  }

  const finalProps: {
    remarkPlugins: unknown[];
    rehypePlugins: unknown[];
    components: ReturnType<typeof createComponents>;
    unwrapDisallowed: boolean;
    allowedElements: string[];
  } = {
    remarkPlugins: memoizedPlugins,
    rehypePlugins: memoizedRehypePlugins,
    components: memoizedComponents,
    unwrapDisallowed: true,
    allowedElements: memoizedAllowedElements,
  };

  return <RagnarMarkdown {...finalProps}>{text}</RagnarMarkdown>;
};

export default StringParser;
